'use client'
/**
 * TransparencyPanel — the PUBLIC monthly category-totals transparency section (Pro).
 *
 * Renders committee-APPROVED summaries only (data SSR-fed via `periods`, most-recent first — never
 * refetched here, never fabricated). Category TOTALS only — never line items or names. Honest-empty
 * when nothing is published. Contribute reuses DonateFlow (masjids.upi_id via the verified-gated donate
 * path) and is hidden entirely when the masjid has no verified UPI / donationEngine off — never a dead CTA.
 *
 * Theme: uses the page's CSS theme tokens (--bg-card, --text-primary, --border-light) so it stays
 * consistent and flips if a dark theme is added; brand tokens (green, gold) plus --danger for the
 * figures and bars.
 */
import type { CSSProperties, ReactNode } from 'react'
import DonateFlow from './DonateFlow'

export type TransparencyPeriod = {
  period: string
  categoryTotals: Array<{ category: string; type: 'income' | 'expense'; total: number }>
  income: number
  expense: number
  balance: number
  approvedAt: string | null
}

const GREEN = '#0E7C61'
const GOLD = '#D4AF37'
const NUM: CSSProperties = { fontVariantNumeric: 'tabular-nums' }

const inr = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`

function monthLabel(period: string): string {
  const [y, m] = period.split('-').map(Number)
  if (!y || !m || m < 1 || m > 12) return period
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}
function fmtDate(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

function Bars({ items, total, color }: { items: TransparencyPeriod['categoryTotals']; total: number; color: string }) {
  if (items.length === 0) {
    return <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '4px 0 0' }}>Nothing recorded this month.</p>
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 8 }}>
      {items.map((it) => {
        const pct = total > 0 ? Math.max(3, Math.round((it.total / total) * 100)) : 0
        return (
          <div key={`${it.type}:${it.category}`} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ flex: '0 0 96px', fontSize: 13, color: 'var(--text-secondary)', fontWeight: 600 }}>{it.category}</span>
            <span style={{ flex: 1, height: 8, borderRadius: 999, background: 'var(--bg-subtle)', overflow: 'hidden' }}>
              <span style={{ display: 'block', height: '100%', width: `${pct}%`, background: color, borderRadius: 999 }} />
            </span>
            <span style={{ flex: '0 0 auto', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', ...NUM }}>{inr(it.total)}</span>
          </div>
        )
      })}
    </div>
  )
}

export default function TransparencyPanel({
  periods,
  masjidId,
  masjidName,
  donateEnabled,
}: {
  periods: TransparencyPeriod[]
  masjidId: string
  masjidName: string
  donateEnabled: boolean
}) {
  // Honest-empty — a single calm line, never zeros or a fake panel.
  if (!periods || periods.length === 0) {
    return (
      <section style={{ marginTop: 24 }}>
        <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: 0 }}>This masjid hasn’t published a monthly summary yet.</p>
      </section>
    )
  }

  const latest = periods[0]
  const older = periods.slice(1)
  const deficit = latest.balance < 0
  const income = [...latest.categoryTotals].filter((c) => c.type === 'income' && c.total > 0).sort((a, b) => b.total - a.total)
  const expense = [...latest.categoryTotals].filter((c) => c.type === 'expense' && c.total > 0).sort((a, b) => b.total - a.total)

  const tile = (label: string, value: number, color: string): ReactNode => (
    <div style={{ flex: 1, minWidth: 96, background: 'var(--bg-subtle)', border: '1px solid var(--border-light)', borderRadius: 12, padding: '12px 14px' }}>
      <p style={{ margin: '0 0 3px', fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 700 }}>{label}</p>
      <p style={{ margin: 0, fontSize: 20, fontWeight: 800, color, ...NUM }}>{inr(value)}</p>
    </div>
  )

  return (
    <section style={{ marginTop: 24, background: 'var(--bg-card)', border: '1px solid var(--border-light)', borderRadius: 16, padding: 20 }}>
      {/* Header + committee-verified badge */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>Monthly transparency</h3>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#E6F4EF', color: GREEN, fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 999, border: `1px solid ${GREEN}22` }}>
          <span aria-hidden style={{ fontSize: 12 }}>✓</span> Committee-verified
        </span>
      </div>
      <p style={{ margin: '4px 0 16px', fontSize: 13, color: 'var(--text-muted)' }}>
        {monthLabel(latest.period)}{latest.approvedAt ? ` · published ${fmtDate(latest.approvedAt)}` : ''}
      </p>

      {/* Stat tiles */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {tile('Received', latest.income, GREEN)}
        {tile('Spent', latest.expense, 'var(--danger)')}
        {tile('Balance', latest.balance, deficit ? 'var(--danger)' : 'var(--text-primary)')}
      </div>

      {/* Where it came from / went */}
      <div style={{ marginTop: 18 }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 800, color: 'var(--text-primary)' }}>Where it came from</p>
        <Bars items={income} total={latest.income} color={GREEN} />
      </div>
      <div style={{ marginTop: 16 }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 800, color: 'var(--text-primary)' }}>Where it went</p>
        <Bars items={expense} total={latest.expense} color={GOLD} />
      </div>

      {/* Deficit line — only from real approved data */}
      {deficit && (
        <p style={{ margin: '18px 0 0', fontSize: 14, fontWeight: 700, color: 'var(--danger)', ...NUM }}>
          This month we’re short {inr(Math.abs(latest.balance))}.
        </p>
      )}

      {/* Footer: older months + Contribute (hidden if no verified UPI / engine off) */}
      {(older.length > 0 || donateEnabled) && (
        <div style={{ marginTop: deficit ? 12 : 18, paddingTop: 16, borderTop: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          {older.length > 0 ? (
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
              Also published: {older.map((p) => monthLabel(p.period)).join(' · ')}
            </p>
          ) : <span />}
          {donateEnabled ? (
            <div style={deficit ? { transform: 'scale(1.03)', transformOrigin: 'right center' } : undefined}>
              <DonateFlow masjidId={masjidId} masjidName={masjidName} />
            </div>
          ) : null}
        </div>
      )}
    </section>
  )
}
